import { TFunction } from 'i18next';
import { TargetIcon } from '../SvgIcons/TargetIcon';
import { useUploadDroppedFile } from '@/utils/upload-drop-file.utils';
import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button';
import { CameraIcon } from '../SvgIcons/CameraIcon';
import { uploadFileToFolder } from '@/modules/VehiculeCheckResult/utils/result-image.utils';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

interface ImageUploadProps {
  t?: TFunction<undefined>;
  initialImage?: string;
  initialImages?: string[];
  multiple?: boolean;
  allowedFormats?: string[];
  minimumImageWidth?: string;
  minimumImageHeight?: string;
  maximumImageWidth?: string;
  maximumImageHeight?: string;
  maxImageSize?: number; // Size in MB, defaults to 2MB
  disabledChooseAnotherImgButton?: boolean;
  chooseAnotherImgContainerClassName?: string;
  mutateUploadImage?: (data: unknown) => void;
  onClick?: () => void;
  onImagesChange?: (hasImages: boolean) => void;
}

export interface ImageUploadRef {
  resetImages: () => void;
  images: string[];
}

export const ImageUpload = forwardRef<ImageUploadRef, ImageUploadProps>(
  (
    {
      t,
      initialImage,
      initialImages,
      allowedFormats,
      multiple = false,
      minimumImageWidth,
      minimumImageHeight,
      maximumImageWidth,
      maximumImageHeight,
      maxImageSize = 2,
      disabledChooseAnotherImgButton,
      chooseAnotherImgContainerClassName,
      onClick,
      mutateUploadImage,
      onImagesChange,
    },
    ref
  ) => {
    const getMaxSizeText = () => {
      let text = '';

      if (multiple) {
        text = t('common:sizeMaxPerImageMb', { size: maxImageSize });
      } else {
        text = t('common:sizeMaxMb', { size: maxImageSize });
      }

      return text;
    };

    const {
      uploadedImage: chosenImage,
      uploadedImages: chosenImages,
      errorMessage: chosenImageErrorMessage,
      setUploadedImage,
      setUploadedImages,
      removeImage,
      chooseFileInputRef,
      handleFileDragOver,
      handleFileDrop,
      handleFileChange,
      handleClickChooseImage,
    } = useUploadDroppedFile({
      t,
      allowedFormats,
      minimumImageWidth,
      minimumImageHeight,
      maximumImageWidth,
      maximumImageHeight,
      maxImageSize,
      maxSizeText: getMaxSizeText(),
      multiple,
    });

    useEffect(() => {
      if (multiple && initialImages && chosenImages.length === 0) {
        setUploadedImages(initialImages);
      } else if (!multiple && initialImage && !chosenImage) {
        setUploadedImage(initialImage);
      }
    }, [
      initialImage,
      initialImages,
      chosenImage,
      chosenImages,
      setUploadedImage,
      setUploadedImages,
      multiple,
    ]);

    useEffect(() => {
      if (chosenImageErrorMessage) {
        notify(chosenImageErrorMessage, NOTIFY.ERROR);
      }
    }, [chosenImageErrorMessage]);

    useEffect(() => {
      if (multiple && chosenImages.length > 0 && mutateUploadImage) {
        const uploadFiles = async (imageUrls: string[]) => {
          try {
            for (const imageUrl of imageUrls) {
              await uploadFileToFolder({
                fileUrl: imageUrl,
                mutate: mutateUploadImage,
              });
            }
          } catch (error) {
            console.error('Error uploading files:', error);
          }
        };

        uploadFiles(chosenImages);
      } else if (
        !multiple &&
        chosenImage &&
        mutateUploadImage &&
        chosenImage !== initialImage
      ) {
        const uploadFile = async (fileUrl: string) => {
          try {
            await uploadFileToFolder({
              fileUrl,
              mutate: mutateUploadImage,
            });
          } catch (error) {
            console.error('Error uploading file:', error);
          }
        };

        uploadFile(chosenImage);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenImage, chosenImages, mutateUploadImage, multiple]);

    const hasImages = multiple ? chosenImages.length > 0 : chosenImage;

    useEffect(() => {
      onImagesChange?.(!!hasImages);
    }, [hasImages, onImagesChange]);

    useImperativeHandle(ref, () => ({
      resetImages: () => {
        if (multiple) {
          setUploadedImages([]);
        } else {
          setUploadedImage('');
        }
      },
      images: multiple ? chosenImages : chosenImage ? [chosenImage] : [],
    }));

    return (
      <>
        {hasImages && (
          <div
            className={twMerge(
              'mb-6 flex flex-1 justify-center gap-3',
              chooseAnotherImgContainerClassName
            )}
          >
            <div>
              <Button
                buttonClassName="px-5 py-4.5 flex-1 border-2 border-purple-5433FF rounded-full bg-white"
                text={
                  multiple
                    ? t('common:addMoreImages')
                    : t('common:chooseAnotherImage')
                }
                textClassName="text-purple-5433FF"
                leftIcon={
                  <CameraIcon className="-mt-1 w-6.5 h-6.5 [&>path]:fill-purple-5433FF" />
                }
                disabled={disabledChooseAnotherImgButton}
                onClick={handleClickChooseImage}
              />
            </div>
          </div>
        )}

        {/* Multiple Images Display */}
        {multiple && chosenImages.length > 0 && (
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[200px]"
            onDragOver={handleFileDragOver}
            onDrop={handleFileDrop}
          >
            {chosenImages.map((image, index) => (
              <div key={index} className="relative group">
                <img
                  src={image}
                  alt={`${t('common:uploadedImage')} ${index + 1}`}
                  className="w-full h-[200px] object-cover rounded-lg"
                />
                <button
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 min-h-[200px]">
              <div className="text-center text-gray-500">
                <TargetIcon className="w-8 h-8 mx-auto mb-2 [&>path]:fill-gray-400" />
                <div className="text-sm">{t('common:dragAndDropImages')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Only show main drag area for single images or when no images in multiple mode */}
        {(!multiple || !hasImages) && (
          <div
            onDragOver={handleFileDragOver}
            onDrop={handleFileDrop}
            className={twMerge(
              'flex flex-col justify-center items-center border-2 border-gray-300 border-dashed rounded-lg',
              !multiple && hasImages && 'border-0 bg-black'
            )}
          >
            {!multiple && chosenImage ? (
              <img
                src={chosenImage}
                alt={t('common:uploadedImage')}
                className="w-full h-[350px] object-contain rounded-lg"
              />
            ) : !hasImages ? (
              <div className="p-8">
                <div className="flex justify-center items-center">
                  <TargetIcon className="w-10 h-10 [&>path]:fill-gray-1E1E1E" />
                </div>
                <div className="mt-3 flex justify-center text-gray-1E1E1E text-xl font-medium">
                  {multiple
                    ? t('common:dragAndDropImages')
                    : t('common:dragAndDropImg')}
                </div>
                <div className="mt-3 flex justify-center text-gray-1E1E1E text-sm uppercase">
                  {t('common:or')}
                </div>
                <div
                  className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm hover:cursor-pointer"
                  onClick={onClick || handleClickChooseImage}
                >
                  {multiple
                    ? t('common:searchFolderAndUploadImages')
                    : t('common:searchFolderAndUploadImage')}
                </div>
                <div className="mt-3 text-gray-600 text-2sm text-center">
                  {getMaxSizeText()}
                  <br />
                  {`${t('common:format')} ${allowedFormats.join(
                    `, ${t('common:or').toLowerCase()} `
                  )}.`}
                </div>
              </div>
            ) : null}
          </div>
        )}

        <input
          type="file"
          ref={chooseFileInputRef}
          className="hidden"
          accept={allowedFormats
            .map((format) => `.${format.toLowerCase()}`)
            .join(',')}
          multiple={multiple}
          onChange={handleFileChange}
        />
      </>
    );
  }
);
